import { FC, useMemo, useRef, useEffect, useState } from 'react';
import { formatSecondsForReminder } from '../PlayerServices';
import {
  PreviewThumbsProps,
  GridType,
  Frame,
} from '../PlayerModels';
import debounce from 'lodash/debounce';
import { ThumbCurrentTime, ThumbFrame, ThumbWrapper } from '../styles';

const PreviewThumbs: FC<PreviewThumbsProps> = ({ vttUrl, show, currentTime, duration, parsedVtt }) => {
  const { thumbnailMap, interval, grid, sprites } = parsedVtt;

  const baseUrl = useMemo(() => vttUrl.split('web.vtt')[0], [vttUrl]);
  const key = useMemo(() => Math.floor(currentTime / interval), [interval, currentTime]);
  const currentThumb = thumbnailMap?.[key];

  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{left: string | number; right: string | number}>({ left: 0, right: 'auto' });
  const [isReady, setIsReady] = useState(false);

  const updatePosition = debounce(() => {
    if (!wrapperRef.current || !containerRef.current) return;

    const thumbWidth = wrapperRef.current.clientWidth || 0;
    const containerWidth = containerRef.current.clientWidth || 0;

    const leftPos = duration && (currentTime / duration) * containerWidth - thumbWidth / 2;

    let overflow: 'left' | 'right' | null = null;
    if (leftPos < 0) overflow = 'left';
    if (leftPos + thumbWidth > containerWidth) overflow = 'right';

    switch (overflow) {
      case 'left':
        setPosition({ left: 0, right: 'auto' });
        break;
      case 'right':
        setPosition({ left: 'auto', right: 0 });
        break;
      default:
        setPosition({ left: leftPos, right: 'auto' });
        break;
    }

    setIsReady(true);
  }, 100);

  useEffect(() => {
    if (show) {
      updatePosition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime, duration, show]);

  if (!currentThumb || currentTime === null) return null;

  return (
    <div ref={containerRef}>
      <ThumbWrapper ref={wrapperRef} show={show && isReady} style={position}>
        <Preloader sources={sprites.map((s) => baseUrl + s)} />
        <Thumbnail baseUrl={baseUrl} {...currentThumb} grid={grid} />
        <ThumbCurrentTime>{formatSecondsForReminder(currentTime)}</ThumbCurrentTime>
      </ThumbWrapper>
    </div>
  );
};

const Preloader = ({ sources }: { sources: string[] }) => (
  <>
    {sources.map((src) => (
      <div key={src} style={{ backgroundImage: `url(${src})` }} />
    ))}
  </>
);

const Thumbnail = ({ imagePath, x, y, width, height, baseUrl, grid }: Frame & { baseUrl: string; grid: GridType }) => {
  const { rows, cols } = grid;
  const offsetX = ((x / width) * 100) / (rows - 1);
  const offsetY = ((y / height) * 100) / (cols - 1);
  const ratio = height / width;
  const imageUrl = baseUrl + imagePath;

  return (
    <ThumbFrame ratio={ratio} src={imageUrl} cols={cols} rows={rows} offsetX={offsetX} offsetY={offsetY} />
  );
};
export default PreviewThumbs;
